import React from "react";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import {
  FaWhatsapp,
  FaComments,
  FaEnvelope,
  FaPhone,
  FaSkype,
  FaDesktop,
  FaMapMarkerAlt,
  FaHeadset,
  FaClock,
  FaCheckCircle,
  FaArrowRight,
} from "react-icons/fa";

import supportImage from "../assets/supportImage.jpg";
import SEO from "../layout/SEO";

const supportOptions = [
  {
    title: "WhatsApp Support",
    icon: <FaWhatsapp />,
    color: "from-green-500 to-green-700",
    description: "Quick responses via WhatsApp",
  },
  {
    title: "Chat with Experts",
    icon: <FaComments />,
    color: "from-blue-500 to-blue-700",
    description: "Live chat with our team",
  },
  {
    title: "Email Support",
    icon: <FaEnvelope />,
    color: "from-red-500 to-red-700",
    description: "Get detailed responses via email",
  },
  {
    title: "Calling Support",
    icon: <FaPhone />,
    color: "from-purple-500 to-purple-700",
    description: "Speak directly with our team",
  },
  {
    title: "Skype Support",
    icon: <FaSkype />,
    color: "from-cyan-500 to-cyan-700",
    description: "Video call support on Skype",
  },
  {
    title: "Remote Desk Support",
    icon: <FaDesktop />,
    color: "from-orange-500 to-orange-700",
    description: "Screen sharing assistance",
  },
  {
    title: "Visit Support",
    icon: <FaMapMarkerAlt />,
    color: "from-pink-500 to-pink-700",
    description: "In-person support at office",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: "easeOut" } },
};

const SupportPage = () => {
  const handleSupportClick = (type) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to continue with ${type}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Continue",
      background: "#fefefe",
      customClass: {
        confirmButton: 'rounded-full px-6 py-2',
        cancelButton: 'rounded-full px-6 py-2',
      }
    }).then((result) => {
      if (result.isConfirmed) {
        switch (type) {
          case "WhatsApp Support":
            window.open("https://wa.me/9326870893", "_blank");
            break;
          case "Chat with Experts":
            Swal.fire("Chat Support", "Live chat expert will connect shortly...", "info");
            break;
          case "Email Support":
            Swal.fire({
              title: "Choose Email Address",
              html: `
                <div style="display: flex; flex-direction: column; gap: 12px;">
                  <button id="mail1" class="swal2-confirm swal2-styled" style="background:#2563eb;">sales@sapeagleerp.com</button>
                  <button id="mail2" class="swal2-confirm swal2-styled" style="background:#2563eb;">support@sapeagleerp.com</button>
                  <button id="mail3" class="swal2-confirm swal2-styled" style="background:#2563eb;">admin@sapeagleerp.com</button>
                  <button id="mail4" class="swal2-confirm swal2-styled" style="background:#2563eb;">info@sapeagleerp.com</button>
                </div>
              `,
              showConfirmButton: false,
              showCancelButton: true,
              cancelButtonText: "Close",
            });
            setTimeout(() => {
              document.getElementById("mail1").onclick = () =>
                window.location.href = "mailto:sales@sapeagleerp.com?subject=Support Request&body=Hello Team,";
              document.getElementById("mail2").onclick = () =>
                window.location.href = "mailto:support@sapeagleerp.com?subject=Support Request&body=Hello Team,";
              document.getElementById("mail3").onclick = () =>
                window.location.href = "mailto:admin@sapeagleerp.com?subject=Support Request&body=Hello Team,";
              document.getElementById("mail4").onclick = () =>
                window.location.href = "mailto:info@sapeagleerp.com?subject=Support Request&body=Hello Team,";
            }, 200);
            break;
          case "Calling Support":
            window.location.href = "tel:+919958790809";
            break;
          case "Skype Support":
            window.open("skype:live:.cid.sample1234?call", "_self");
            break;
          case "Remote Desk Support":
            Swal.fire("Remote Desk", "Our technician will connect via AnyDesk / TeamViewer shortly.", "success");
            break;
          case "Visit Support":
            Swal.fire("Office Location", "You can visit us at: \nNew Delhi, India", "info");
            break;
          default:
            break;
        }
      }
    });
  };

  const jsonLD = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "SAPEAGLE ERP - Support Page",
    "operatingSystem": "Web",
    "applicationCategory": "BusinessApplication",
    "description": "SAPEAGLE ERP Support — Contact us for help regarding CRM, HRMS, Payroll, Accounting, Inventory & Sales Management software.",
    "offers": {
      "@type": "Offer",
      "price": "0.00",
      "priceCurrency": "INR",
      
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-16 md:py-24">
      <SEO
        title="Support Page - SAPEAGLE ERP | Contact & Help"
        description="Need help with SAPEAGLE ERP? Contact our support team via WhatsApp, Email, Chat, Phone, Skype, Remote Desk or Visit our office."
       
        keywords="SAPEAGLE ERP support, ERP software help, CRM software support, HRMS software support, Payroll software help, Accounting software support, Inventory support"
    
        jsonLD={jsonLD}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-6"
          >
            <FaHeadset className="text-blue-600" />
            <span className="text-blue-700 text-sm font-medium">24/7 Support</span>
          </motion.div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            We're Here to{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Help You
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Our dedicated support team is always ready to assist you through multiple channels.
          </p>
        </motion.div>

        {/* Main Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid lg:grid-cols-2 gap-8 lg:gap-12"
        >
          {/* Left - Image Section */}
          <motion.div variants={itemVariants} className="relative">
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
              className="relative rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src={supportImage}
                alt="Support"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-white text-2xl font-bold mb-2">Premium Support</h3>
                <p className="text-white/80">Expert assistance whenever you need it</p>
              </div>
            </motion.div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              {[
                { label: "Response Time", value: "< 1 Hour", icon: <FaClock /> },
                { label: "Satisfaction", value: "99%", icon: <FaCheckCircle /> },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-2xl p-4 shadow-lg text-center"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2 text-blue-600">
                    {stat.icon}
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Support Options */}
          <motion.div variants={itemVariants} className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Choose Your Support Channel
              </h2>
              <p className="text-gray-600 mb-6">
                Select the support option that best suits your needs. Our team is ready to help!
              </p>
            </div>

            {/* Support Options Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {supportOptions.map((option, index) => (
                <motion.button
                  key={index}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSupportClick(option.title)}
                  className="group bg-white rounded-2xl p-5 shadow-md hover:shadow-xl transition-all duration-300 text-left border border-gray-100 hover:border-transparent relative overflow-hidden"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${option.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                  
                  <div className="flex items-start gap-4 relative">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${option.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <span className="text-white text-xl">{option.icon}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {option.title}
                      </h3>
                      <p className="text-sm text-gray-500">{option.description}</p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Contact Info */}
            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white mt-6"
            >
              <h3 className="text-lg font-bold mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <FaEnvelope className="text-white/80" />
                  <span className="text-white/90">info@sapeagleerp.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <FaPhone className="text-white/80" />
                  <span className="text-white/90">+91 9910680809, </span>
                </div>
                <div className="flex items-center gap-3">
                  <FaMapMarkerAlt className="text-white/80" />
                  <span className="text-white/90">New Delhi, India</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default SupportPage;
